import { RiArrowLeftLine, RiQuestionLine } from "@remixicon/react";
import classNames from "classnames";
import React, { ReactElement, useEffect, useState } from "react";
import { RiArrowRightLine } from "react-icons/ri";
import useRouting, { NavLink, useParams } from "../../contexts/Routing";

import AddChannel from "./steps/add-channel";
import AddChannelOptions from "./steps/add-channel-options";

import PickMode from "./steps/contact-mode";
import PickRole from "./steps/pick-role";
import TicketingSystem from "./steps/ticketing-system";
import EnableAI from "./steps/enable-ai";

import { ReactComponent as FoqalLogo } from "../../assets/img/foqal-logo.svg";

import {
    OnboardingProfile,
    useFinishOnboardingMutation,
    useGetOnboardingProfileQuery, useSaveOnboardingProfileMutation,
} from "../../__generated__/api";
import { removeTypename } from "../../api";
import useLogging from "../../contexts/logging/LoggingContext";
import { Themes, useTheme } from "../../contexts/Theme";
import { StepProps } from "./steps/base";

import Help from "../../components/help/Help";
import Button from "../../components/theme/button/Button";
import { useHelpContext } from "../../contexts/HelpContext";
import Styles from "./Onboarding.module.scss";
import RouterLink from "../../components/theme/button/RouterLink";

interface Step {
    id: string;
    step: (props: StepProps) => ReactElement;
    next?: string;
    isCounted: boolean;
    canSkip: boolean;
    previous?: string;
}

const ONBOARDING_DONE = "onboarding-done";

const STEPS_V3: Step[] = [
    {
        id: "pick-role",
        step: PickRole,
        isCounted: true,
        canSkip: true,
        next: "enable-ai",
    },
    {
        id: "enable-ai",
        step: EnableAI,
        isCounted: true,
        canSkip: false,
        previous: "pick-role",
        next: "install-ai",
    },
    {
        id: "install-ai",
        step: EnableAI,
        isCounted: false,
        canSkip: false,
        previous: "enable-ai",
        next: "contact-mode",
    },
    {
        id: "contact-mode",
        step: PickMode,
        previous: "enable-ai",
        isCounted: true,
        canSkip: true,
        next: "ticketing-system",
    },
    {
        id: "ticketing-system",
        step: TicketingSystem,
        previous: "contact-mode",
        isCounted: true,
        canSkip: true,
        // next: "common-tasks",
        next: "add-to-slack-options",
    },
    // {
    //     id: "common-tasks",
    //     step: CommonTasks,
    //     previous: "ticketing-system",
    //     isCounted: true,
    //     canSkip: true,
    //     next: "add-to-slack-options",
    // },
    {
        id: "add-to-slack-options",
        step: AddChannelOptions,
        // previous: "common-tasks",
        previous: "ticketing-system",
        isCounted: true,
        canSkip: false,
        next: "add-to-slack",
    },
    {
        id: "add-to-slack",
        step: AddChannel,
        isCounted: false,
        canSkip: false,
        previous: "add-to-slack-options",
        next: ONBOARDING_DONE,
    },

];

const COUNTED_STEPS = STEPS_V3.filter(current => current.isCounted);

const STEPS_LENGTH = COUNTED_STEPS.length;

export default function Onboarding() : ReactElement | null {
    const { Paths, historyPushWithSearch, historyPush } = useRouting();
    const [profile, setProfile] = useState<OnboardingProfile>({});

    const { data: dbProfile } = useGetOnboardingProfileQuery();
    const [saveProfile] = useSaveOnboardingProfileMutation();
    const [finishOnboarding] = useFinishOnboardingMutation();
    const logging = useLogging();

    const [lastReadyStepId, setLastReadyStep] = useState("");

    const theme = useTheme();

    const isV3 = theme.isPageV3(Paths.ONBOARDING);

    useEffect(() => {
        if (dbProfile) {
            setProfile(dbProfile.onboardingProfile ?? {});
        }
    }, [dbProfile]);

    const { stepId } = useParams(Paths.ONBOARDING_STEP);

    const stepIndex = (stepId ? STEPS_V3.findIndex(current => current.id === stepId) : undefined) ?? 0;
    const step = STEPS_V3[stepIndex];
    const Step = step.step;

    const onDoneOnboarding = async () => {
        await finishOnboarding();
        historyPushWithSearch(Paths.ADMINISTRATOR_SETTING, { isSuccess: "✔️", isOnboarded: "✔️" }, { setting: "home" });
    };
    const storeProfile = async (currentProfile: OnboardingProfile) => {
        logging.wrap(async () => {
            const updatedProfile = await saveProfile({
                variables: {
                    profile: removeTypename({
                        ...currentProfile,
                        isExternalSupport: undefined,
                    }),
                },
            });
            setProfile({
                ...currentProfile,
                isExternalSupport: updatedProfile.data?.saveOnboardingProfile?.isExternalSupport,
            });
        });
        setProfile(currentProfile);
    };

    useEffect(() => {
        if (isV3) {
            theme.setTheme(Themes.LIGHT);
        }
    }, [isV3]);

    const isLastStep = step.next === ONBOARDING_DONE || lastReadyStepId === ONBOARDING_DONE;

    const helpArticleId = useHelpContext().getArticleId();

    return (

        <div className={Styles["page-container"]}>
            <div className={Styles["page-container-top"]}>
                <FoqalLogo />
                {helpArticleId && (
                    <div>
                        <Help
                            button={<RiQuestionLine />}
                            metricPathPrefix={["onboarding", stepId, "help"]}
                            buttonClassName="btn btn-none"
                            articleId={helpArticleId}
                        />
                    </div>
                )}

            </div>
            <div>
                <div className={Styles.progress}>
                    <div>
                        {Math.min(stepIndex + 1, STEPS_LENGTH)}
                        /
                        {STEPS_LENGTH}
                    </div>
                    <div className={Styles["progress-bar"]}>
                        {COUNTED_STEPS.map((__, index) => (
                            <div className={classNames(
                                Styles["progress-bar-block"],
                                index <= stepIndex ? Styles["progress-bar-block-done"] : "",
                            )}
                            />
                        ))}
                    </div>
                </div>

                <Step
                    isV3
                    profile={profile}
                    onSaveProfile={currentProfile => {
                        storeProfile(currentProfile);
                    }}
                    stepId={stepId}
                    goBack={() => {
                        const previous = STEPS_V3.find(current => current.id === stepId)?.previous as string;
                        historyPush(Paths.ONBOARDING_STEP, { stepId: previous });
                    }}
                    goNext={() => {
                        const previous = STEPS_V3.find(current => current.id === stepId)?.next as string;
                        historyPush(Paths.ONBOARDING_STEP, { stepId: previous });
                    }}
                    onReset={async () => {
                        setLastReadyStep(STEPS_V3.find(current => current.id === stepId)?.previous as string);
                    }}
                    onDone={async currentProfile => {
                        setLastReadyStep(ONBOARDING_DONE);
                        await storeProfile({
                            ...currentProfile,
                            stepsCompleted: [...profile.stepsCompleted ?? [], step.id],
                        });
                    }}
                    onNext={async currentProfile => {
                        setLastReadyStep(stepId);
                        await storeProfile({
                            ...currentProfile,
                            stepsCompleted: [...profile.stepsCompleted ?? [], step.id],
                        });
                    }}
                />

                <div className={Styles["page-actions"]}>
                    <div>
                        {step.previous && (
                            <RouterLink
                                to={Paths.ONBOARDING_STEP}
                                params={{ stepId: step.previous }}
                                variant="outline-blue"
                                size="large"
                                metricPath={["onboarding", stepId, "back"]}
                                onClick={() => {
                                    setLastReadyStep(step.previous as string);
                                }}
                            >
                                <span className="btn-icon btn-prefix-icon">
                                    <RiArrowLeftLine />
                                </span>
                                Back
                            </RouterLink>
                        )}

                    </div>
                    <div>
                        {step.canSkip && step.next && !isLastStep && (
                            <RouterLink
                                to={Paths.ONBOARDING_STEP}
                                params={{ stepId: step.next }}
                                variant="ghost-blue"
                                size="large"
                                metricPath={["onboarding", stepId, "skip"]}
                            >
                                Skip
                            </RouterLink>
                        )}

                        {step.next && !isLastStep && profile.stepsCompleted?.includes(step.id) && (
                            <RouterLink
                                size="large"
                                to={Paths.ONBOARDING_STEP}
                                params={{ stepId: step.next }}
                                variant="primary"
                                className={Styles["btn-next"]}
                                metricPath={["onboarding", stepId, "continue"]}
                            >
                                Continue
                                <span className="btn-icon btn-postfix-icon">
                                    <RiArrowRightLine />
                                </span>
                            </RouterLink>
                        )}

                        {isLastStep && (
                            <Button
                                variant="primary"
                                className={Styles["btn-next"]}
                                onClick={onDoneOnboarding}
                                metricPath={["onboarding", stepId, "done"]}
                            >
                                Finished Setup
                            </Button>
                        )}

                    </div>
                </div>

            </div>
        </div>

    );
}
